import { env } from 'configs/EnvironmentConfig';
import { parseOAuthAuthError } from 'utils/googleAuthUtils';

export const LINKEDIN_OAUTH_STATE_KEY = 'vlm_linkedin_oauth_state';
const LINKEDIN_AUTH_URL = 'https://www.linkedin.com/oauth/v2/authorization';
const LINKEDIN_SCOPES = 'openid profile email';

/** Parse a LinkedIn (MSAL/backend) auth error into the shared OAuth error shape. */
export const parseLinkedInAuthError = (err) =>
	parseOAuthAuthError(err, 'LinkedIn authentication failed');

/** Resolve redirect URI — must match backend LINKEDIN_REDIRECT_URI and LinkedIn app settings. */
export const getLinkedInRedirectUri = () => {
	const configured = (env.LINKEDIN_REDIRECT_URI || '').trim();
	if (configured) {
		return configured.replace(/\/$/, '');
	}
	const origin = (env.FRONTEND_URL || env.PUBLIC_URL || window.location.origin || '').replace(/\/$/, '');
	return `${origin}/auth/linkedin/callback`;
};

export const createLinkedInOAuthState = () => {
	const bytes = new Uint8Array(16);
	crypto.getRandomValues(bytes);
	return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

export const storeLinkedInOAuthState = (payload) => {
	sessionStorage.setItem(LINKEDIN_OAUTH_STATE_KEY, JSON.stringify(payload));
};

export const readLinkedInOAuthState = () => {
	try {
		const raw = sessionStorage.getItem(LINKEDIN_OAUTH_STATE_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
};

export const clearLinkedInOAuthState = () => {
	sessionStorage.removeItem(LINKEDIN_OAUTH_STATE_KEY);
};

/** Validate and consume stored OAuth state (single use). */
export const consumeLinkedInOAuthState = (receivedState) => {
	const stored = readLinkedInOAuthState();
	clearLinkedInOAuthState();
	if (!stored?.state || !receivedState || stored.state !== receivedState) {
		return null;
	}
	return stored;
};

/** Build POST /auth/linkedin body — sends authorization code (not id_token). */
export const buildLinkedInRequestBody = (code, mode = 'login', signupFields = {}) => {
	const body = { code, mode };
	if (mode === 'signup' && signupFields && typeof signupFields === 'object') {
		Object.assign(body, signupFields);
	}
	return body;
};

export const buildLinkedInOAuthUrl = ({
	mode = 'login',
	signupFields = {},
	returnPath = '',
} = {}) => {
	const clientId = (env.LINKEDIN_CLIENT_ID || '').trim();
	if (!clientId) {
		return '';
	}

	const state = createLinkedInOAuthState();
	storeLinkedInOAuthState({
		state,
		mode,
		signupFields,
		returnPath,
	});

	const params = new URLSearchParams({
		response_type: 'code',
		client_id: clientId,
		redirect_uri: getLinkedInRedirectUri(),
		scope: LINKEDIN_SCOPES,
		state,
	});

	return `${LINKEDIN_AUTH_URL}?${params.toString()}`;
};
